import React, { useState, useMemo } from "react";
import styles from "./userRecordingSummary.module.css";
import {
  TMeetingRecord,
  TTextAnalysis,
  TStructuredMeetingSummary,
  MeetingRecordStatus,
  ProcessingStages,
  ProcessingStageSteps,
  TProcessingStage,
} from "../../services/types/sessionSummary.types";
import { tWithKeyAttr as t } from "../../i18nHelper";
import i18n from "../../i18n";
import TextAnalysisInPopup from "../textAnalysisInPopup/textAnalysisInPopup";
import VoiceAnalysisInPopup from "../voiceAnalysisInPopup/voiceAnalysisInPopup";
import { useExportMeetingSummaryToPdfMutation } from "../../services/api/sessionSummary.api";

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
}

function formatTime(
  dateStr: string | null | undefined,
  utcTime?: string | null,
): string {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    const isMidnight =
      date.getUTCHours() === 0 &&
      date.getUTCMinutes() === 0 &&
      date.getUTCSeconds() === 0;
    if (!isMidnight) {
      return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
    }
    if (utcTime) {
      const datePart = dateStr.split("T")[0];
      const combined = new Date(`${datePart}T${utcTime}:00Z`);
      if (!isNaN(combined.getTime())) {
        return `${String(combined.getHours()).padStart(2, "0")}:${String(combined.getMinutes()).padStart(2, "0")}`;
      }
    }
    return "";
  } catch {
    return "";
  }
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0)
    return `${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

function getCurrentStep(stage: TProcessingStage | null | undefined): number {
  if (!stage) return 0;
  return ProcessingStageSteps[stage] || 0;
}

function getStageLabel(stage: TProcessingStage | null | undefined): string {
  if (!stage) return "";
  const stageLabels: Record<string, string> = {
    [ProcessingStages.UPLOADING]: t("STAGE_UPLOADING"),
    [ProcessingStages.TRANSCRIBING]: t("STAGE_TRANSCRIBING"),
    [ProcessingStages.ANALYZING]: t("STAGE_ANALYZING"),
    [ProcessingStages.FINALIZING]: t("STAGE_FINALIZING"),
    [ProcessingStages.TEXT_ANALYSIS]: t("STAGE_ANALYZING"),
    [ProcessingStages.AUDIO_ANALYSIS]: t("STAGE_ANALYZING"),
    [ProcessingStages.SUMMARIZATION]: t("STAGE_ANALYZING"),
  };
  return stageLabels[stage] || stage;
}

function formatTranscript(transcript: string): React.ReactNode[] {
  if (!transcript) return [];
  const cleaned = transcript.replace(/\*\*/g, "");
  const lines = cleaned.split(/\n+/).filter((line) => line.trim());
  const elements: React.ReactNode[] = [];
  const timestampRegex =
    /^\[(\d{1,2}:\d{2}(?::\d{2})?(?:\s*-\s*\d{1,2}:\d{2}(?::\d{2})?)?)\]\s*/;
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();
    const tsMatch = line.match(timestampRegex);
    if (tsMatch) {
      const timestamp = tsMatch[1];
      const afterTimestamp = line.substring(tsMatch[0].length).trim();
      const speakerMatch = afterTimestamp.match(/^([^:]{1,60}):\s*(.+)/);
      if (speakerMatch) {
        elements.push(
          <div key={elements.length} className={styles.speakerBlock}>
            <div className={styles.speakerHeader}>
              <span className={styles.timestamp}>{timestamp}</span>
              <span className={styles.speakerLabel}>
                {speakerMatch[1].trim()}
              </span>
            </div>
            <div className={styles.speakerText}>{speakerMatch[2]}</div>
          </div>,
        );
      } else {
        elements.push(
          <div key={elements.length} className={styles.speakerBlock}>
            <div className={styles.speakerHeader}>
              <span className={styles.timestamp}>{timestamp}</span>
            </div>
            <div className={styles.speakerText}>{afterTimestamp}</div>
          </div>,
        );
      }
      i++;
      continue;
    }
    const azureSpeakerMatch = line.match(/^\[([^\]]+)\]$/);
    if (azureSpeakerMatch) {
      const speaker = azureSpeakerMatch[1].trim();
      const textLines: string[] = [];
      i++;
      while (i < lines.length) {
        const nextLine = lines[i].trim();
        if (
          nextLine.match(/^\[([^\]]+)\]$/) ||
          nextLine.match(timestampRegex) ||
          nextLine.match(/^([^:]{1,60}):\s*.+/)
        )
          break;
        if (nextLine) textLines.push(nextLine);
        i++;
      }
      elements.push(
        <div key={elements.length} className={styles.speakerBlock}>
          <div className={styles.speakerHeader}>
            <span className={styles.speakerLabel}>{speaker}</span>
          </div>
          <div className={styles.speakerText}>{textLines.join(" ")}</div>
        </div>,
      );
      continue;
    }
    const classicSpeakerMatch = line.match(/^([^:]{1,60}):\s*(.+)/);
    if (classicSpeakerMatch) {
      elements.push(
        <div key={elements.length} className={styles.speakerBlock}>
          <div className={styles.speakerHeader}>
            <span className={styles.speakerLabel}>
              {classicSpeakerMatch[1].trim()}
            </span>
          </div>
          <div className={styles.speakerText}>{classicSpeakerMatch[2]}</div>
        </div>,
      );
      i++;
      continue;
    }
    if (line) {
      elements.push(
        <p key={elements.length} className={styles.transcriptParagraph}>
          {line}
        </p>,
      );
    }
    i++;
  }
  return elements;
}

interface UserRecordingSummaryProps {
  record: TMeetingRecord;
  userGuid: string;
  onClose: () => void;
}

export default function UserRecordingSummary({
  record,
  userGuid,
  onClose,
}: UserRecordingSummaryProps) {
  const [activeTab, setActiveTab] = useState<
    "transcript" | "summary" | "text" | "voice"
  >("summary");
  const [exportPdf, { isLoading: isExporting }] =
    useExportMeetingSummaryToPdfMutation();

  async function handleExportPdf() {
    try {
      const result = await exportPdf({
        userGuid,
        recordNumber: record.recordNumber,
        language: i18n.language || "he",
      }).unwrap();
      if (result.success && result.pdfBase64) {
        const link = document.createElement("a");
        link.href = `data:application/pdf;base64,${result.pdfBase64}`;
        link.download =
          result.fileName || `meeting_summary_${record.recordNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert(result.errorMessage || t("EXPORT_FAILED"));
      }
    } catch (error: any) {
      alert(error?.data?.errorMessage || error?.message || t("EXPORT_FAILED"));
    }
  }

  const structuredSummary = useMemo<TStructuredMeetingSummary | null>(() => {
    const raw = record.meetingSummary || "";
    if (!raw) return null;
    try {
      let jsonStr = raw;
      if (jsonStr.includes("```json"))
        jsonStr = jsonStr.replace(/```json\s*/g, "").replace(/```\s*/g, "");
      else if (jsonStr.includes("```"))
        jsonStr = jsonStr.replace(/```\s*/g, "");
      const jsonMatch = jsonStr.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]) as TStructuredMeetingSummary;
        if (
          parsed.subject !== undefined ||
          parsed.primaryFindings !== undefined
        )
          return parsed;
      }
    } catch {
      /* ignore */
    }
    return null;
  }, [record.meetingSummary]);

  const textData = useMemo<TTextAnalysis | null>(() => {
    if (!record.textAnalyze) return null;
    try {
      const match = record.textAnalyze.match(/\{[\s\S]*\}/);
      return JSON.parse(match ? match[0] : "{}") as TTextAnalysis;
    } catch {
      return null;
    }
  }, [record.textAnalyze]);

  const voiceData = useMemo<any>(() => {
    if (!record.audioAnalyze) return null;
    try {
      return JSON.parse(record.audioAnalyze);
    } catch {
      return null;
    }
  }, [record.audioAnalyze]);

  const isProcessing = record.status === MeetingRecordStatus.PROCESSING;
  const recordDate = record.date || "";
  const recordTime = formatTime(record.date, record.time) || "";
  const displaySubject = structuredSummary?.subject || record.subject || "";
  const currentStep = getCurrentStep(record.processingStage);
  const totalSteps = 4;

  const tabAvailability = {
    transcript: !!record.transcription?.trim(),
    text: !!record.textAnalyze,
    voice: !!record.audioAnalyze,
    summary: !!record.meetingSummary,
  };

  const summaryModalDir = (() => {
    const lng = i18n.resolvedLanguage || i18n.language;
    if (lng && i18n.dir(lng) === "rtl") return "rtl" as const;
    if (typeof lng === "string") {
      const base = lng.split("-")[0].toLowerCase();
      if (base === "he" || base === "iw") return "rtl" as const;
    }
    return "ltr" as const;
  })();

  const rootDirClass =
    summaryModalDir === "rtl" ? styles.rootRtl : styles.rootLtr;

  return (
    <div
      className={`${styles.pageContainer} ${rootDirClass}`}
      dir={summaryModalDir}
    >
      <div className={styles.tabsContainer}>
        <div className={styles.detailHeader}>
          <button className={styles.closeBtn} onClick={onClose}>
            ✕
          </button>
          <div>
            <h2>
              {t("MEETING")} — {formatDate(recordDate)}
            </h2>
            {displaySubject && (
              <p style={{ opacity: 0.9, marginTop: "5px" }}>{displaySubject}</p>
            )}
          </div>
        </div>

        <div className={styles.detailBody}>
          <div className={styles.infoGrid}>
            <div className={styles.infoCard}>
              <div className={styles.infoCardLabel}>
                {t("RECORDINGS_DATE_TIME")}
              </div>
              <div className={styles.infoCardValue}>
                {formatDate(recordDate)}
                {recordTime ? `, ${recordTime}` : ""}
              </div>
            </div>
            <div className={styles.infoCard}>
              <div className={styles.infoCardLabel}>
                {t("RECORDINGS_DURATION")}
              </div>
              <div className={styles.infoCardValue}>
                {record.duration != null ? (
                  formatDuration(record.duration)
                ) : isProcessing ? (
                  <span className={styles.infoCardSpinner} />
                ) : (
                  "—"
                )}
              </div>
            </div>
            <div className={styles.infoCard}>
              <div className={styles.infoCardLabel}>{t("THERAPIST")}</div>
              <div className={styles.infoCardValue}>
                {record.therapistName || "—"}
              </div>
            </div>
            <div className={styles.infoCard}>
              <div className={styles.infoCardLabel}>{t("MEETING_SUBJECT")}</div>
              <div className={styles.infoCardValue}>
                {displaySubject || record.type || "—"}
              </div>
            </div>
          </div>

          {isProcessing && (
            <div className={styles.processingBanner}>
              <span className={styles.processingBannerIcon}>⏳</span>
              <div className={styles.processingBannerText}>
                <div className={styles.processingBannerTitle}>
                  {t("PROCESSING_IN_PROGRESS")}
                </div>
                <div className={styles.processingBannerSubtitle}>
                  {getStageLabel(record.processingStage)} ({currentStep}/
                  {totalSteps})
                </div>
              </div>
              <div className={styles.processingBannerStepper}>
                {[1, 2, 3, 4, 5, 6].map((step) => (
                  <div
                    key={step}
                    className={`${styles.processingBannerStep} ${step <= currentStep ? styles.processingBannerStepActive : ""} ${step === currentStep ? styles.processingBannerStepCurrent : ""}`}
                  />
                ))}
              </div>
            </div>
          )}

          <div className={styles.tabsHeader}>
            <div
              className={`${styles.tabItem} ${activeTab === "transcript" ? styles.active : ""} ${!tabAvailability.transcript && isProcessing ? styles.tabItemDisabled : ""}`}
              onClick={() => setActiveTab("transcript")}
            >
              {t("TRANSCRIPTION")} 📝
              {!tabAvailability.transcript && isProcessing && (
                <span className={styles.tabProcessingBadge} />
              )}
            </div>
            <div
              className={`${styles.tabItem} ${activeTab === "summary" ? styles.active : ""} ${!tabAvailability.summary && isProcessing ? styles.tabItemDisabled : ""}`}
              onClick={() => setActiveTab("summary")}
            >
              {t("MEETING_SUMMARY")} 📋
              {!tabAvailability.summary && isProcessing && (
                <span className={styles.tabProcessingBadge} />
              )}
            </div>
            <div
              className={`${styles.tabItem} ${activeTab === "text" ? styles.active : ""} ${!tabAvailability.text && isProcessing ? styles.tabItemDisabled : ""}`}
              onClick={() => setActiveTab("text")}
            >
              {t("TEXT_ANALSIS")} 📊
              {!tabAvailability.text && isProcessing && (
                <span className={styles.tabProcessingBadge} />
              )}
            </div>
            <div
              className={`${styles.tabItem} ${activeTab === "voice" ? styles.active : ""} ${!tabAvailability.voice && isProcessing ? styles.tabItemDisabled : ""}`}
              onClick={() => setActiveTab("voice")}
            >
              {t("VOICE_ANALSIS")} 🎵
              {!tabAvailability.voice && isProcessing && (
                <span className={styles.tabProcessingBadge} />
              )}
            </div>
          </div>

          <div className={styles.tabContent} dir={summaryModalDir}>
            {activeTab === "voice" &&
              (!tabAvailability.voice && isProcessing ? (
                <div className={styles.processingTabContent}>
                  <div className={styles.processingSpinner} />
                  <div className={styles.processingMessage}>
                    {t("TAB_PROCESSING")}
                  </div>
                </div>
              ) : (
                <div className={styles.tabText}>
                  <VoiceAnalysisInPopup data={voiceData || {}} />
                </div>
              ))}

            {activeTab === "text" &&
              (!tabAvailability.text && isProcessing ? (
                <div className={styles.processingTabContent}>
                  <div className={styles.processingSpinner} />
                  <div className={styles.processingMessage}>
                    {t("TAB_PROCESSING")}
                  </div>
                </div>
              ) : (
                <div className={styles.tabText}>
                  <TextAnalysisInPopup data={textData} />
                </div>
              ))}

            {activeTab === "summary" &&
              (!tabAvailability.summary && isProcessing ? (
                <div className={styles.processingTabContent}>
                  <div className={styles.processingSpinner} />
                  <div className={styles.processingMessage}>
                    {t("TAB_PROCESSING")}
                  </div>
                </div>
              ) : (
                <div className={styles.summaryTab} dir={summaryModalDir}>
                  <div className={styles.summaryContent}>
                    {structuredSummary ? (
                      <div className={styles.summaryBox}>
                        <div className={styles.summaryBoxInner}>
                          {structuredSummary.subject && (
                            <div className={styles.summarySection}>
                              <h4 className={styles.summarySectionTitle}>
                                <span>🎯</span>
                                {t("MEETING_SUBJECT")}
                              </h4>
                              <div className={styles.subjectHighlight}>
                                {structuredSummary.subject}
                              </div>
                            </div>
                          )}
                          {structuredSummary.primaryFindings?.length > 0 && (
                            <div className={styles.summarySection}>
                              <h4 className={styles.summarySectionTitle}>
                                <span>📊</span>
                                {t("PRIMARY_FINDINGS")}
                              </h4>
                              <ul className={styles.summaryList}>
                                {structuredSummary.primaryFindings.map(
                                  (f, i) => (
                                    <li
                                      key={i}
                                      className={styles.summaryListItem}
                                    >
                                      {f}
                                    </li>
                                  ),
                                )}
                              </ul>
                            </div>
                          )}
                          {structuredSummary.decisions?.length > 0 && (
                            <div className={styles.summarySection}>
                              <h4 className={styles.summarySectionTitle}>
                                <span>💊</span>
                                {t("CLINICAL_DECISIONS")}
                              </h4>
                              <ul className={styles.summaryList}>
                                {structuredSummary.decisions.map((d, i) => (
                                  <li
                                    key={i}
                                    className={styles.summaryListItem}
                                  >
                                    {d}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {structuredSummary.recommendations?.length > 0 && (
                            <div className={styles.summarySection}>
                              <h4 className={styles.summarySectionTitle}>
                                <span>📝</span>
                                {t("RECOMMENDATIONS_TO_PATIENT")}
                              </h4>
                              <ul className={styles.summaryList}>
                                {structuredSummary.recommendations.map(
                                  (r, i) => (
                                    <li
                                      key={i}
                                      className={styles.summaryListItem}
                                    >
                                      {r}
                                    </li>
                                  ),
                                )}
                              </ul>
                            </div>
                          )}
                          {structuredSummary.actionItems?.length > 0 && (
                            <div className={styles.summarySection}>
                              <h4 className={styles.summarySectionTitle}>
                                <span>✅</span>
                                {t("ACTION_ITEMS")}
                              </h4>
                              <ul className={styles.summaryList}>
                                {structuredSummary.actionItems.map((a, i) => (
                                  <li
                                    key={i}
                                    className={styles.summaryListItem}
                                  >
                                    {a}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div className={styles.statusGrid}>
                            {structuredSummary.patientStatus &&
                              (structuredSummary.patientStatus
                                .currentCondition ||
                                structuredSummary.patientStatus.progressNotes ||
                                structuredSummary.patientStatus.concerns) && (
                                <div className={styles.summarySection}>
                                  <h4 className={styles.summarySectionTitle}>
                                    <span>🩺</span>
                                    {t("PATIENT_STATUS")}
                                  </h4>
                                  <div className={styles.statusCard}>
                                    {structuredSummary.patientStatus
                                      .currentCondition && (
                                      <div className={styles.statusCardItem}>
                                        <div className={styles.statusCardLabel}>
                                          {t("CURRENT_CONDITION")}
                                        </div>
                                        <div className={styles.statusCardValue}>
                                          {
                                            structuredSummary.patientStatus
                                              .currentCondition
                                          }
                                        </div>
                                      </div>
                                    )}
                                    {structuredSummary.patientStatus
                                      .progressNotes && (
                                      <div className={styles.statusCardItem}>
                                        <div className={styles.statusCardLabel}>
                                          {t("PROGRESS_NOTES")}
                                        </div>
                                        <div className={styles.statusCardValue}>
                                          {
                                            structuredSummary.patientStatus
                                              .progressNotes
                                          }
                                        </div>
                                      </div>
                                    )}
                                    {structuredSummary.patientStatus
                                      .concerns && (
                                      <div className={styles.statusCardItem}>
                                        <div className={styles.statusCardLabel}>
                                          {t("CONCERNS")}
                                        </div>
                                        <div className={styles.statusCardValue}>
                                          {
                                            structuredSummary.patientStatus
                                              .concerns
                                          }
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            {structuredSummary.treatmentPlan &&
                              (structuredSummary.treatmentPlan.current ||
                                structuredSummary.treatmentPlan.modifications ||
                                structuredSummary.treatmentPlan.nextSteps) && (
                                <div className={styles.summarySection}>
                                  <h4 className={styles.summarySectionTitle}>
                                    <span>📋</span>
                                    {t("TREATMENT_PLAN")}
                                  </h4>
                                  <div className={styles.statusCard}>
                                    {structuredSummary.treatmentPlan
                                      .current && (
                                      <div className={styles.statusCardItem}>
                                        <div className={styles.statusCardLabel}>
                                          {t("CURRENT_TREATMENT")}
                                        </div>
                                        <div className={styles.statusCardValue}>
                                          {
                                            structuredSummary.treatmentPlan
                                              .current
                                          }
                                        </div>
                                      </div>
                                    )}
                                    {structuredSummary.treatmentPlan
                                      .modifications && (
                                      <div className={styles.statusCardItem}>
                                        <div className={styles.statusCardLabel}>
                                          {t("MODIFICATIONS")}
                                        </div>
                                        <div className={styles.statusCardValue}>
                                          {
                                            structuredSummary.treatmentPlan
                                              .modifications
                                          }
                                        </div>
                                      </div>
                                    )}
                                    {structuredSummary.treatmentPlan
                                      .nextSteps && (
                                      <div className={styles.statusCardItem}>
                                        <div className={styles.statusCardLabel}>
                                          {t("NEXT_STEPS")}
                                        </div>
                                        <div className={styles.statusCardValue}>
                                          {
                                            structuredSummary.treatmentPlan
                                              .nextSteps
                                          }
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                          </div>
                          {structuredSummary.nextAppointment && (
                            <div className={styles.summarySection}>
                              <h4 className={styles.summarySectionTitle}>
                                <span>📅</span>
                                {t("NEXT_APPOINTMENT")}
                              </h4>
                              <div className={styles.nextAppointmentBadge}>
                                <span>🗓️</span>
                                {structuredSummary.nextAppointment}
                              </div>
                            </div>
                          )}
                          {structuredSummary.additionalNotes && (
                            <div className={styles.summarySection}>
                              <h4 className={styles.summarySectionTitle}>
                                <span>💡</span>
                                {t("ADDITIONAL_NOTES")}
                              </h4>
                              <div className={styles.additionalNotes}>
                                {structuredSummary.additionalNotes}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : record.meetingSummary ? (
                      <div className={styles.tabText}>
                        <div className={styles.plainTextSummary}>
                          {record.meetingSummary}
                        </div>
                      </div>
                    ) : (
                      <div className={styles.emptyStateMessage}>
                        <span className={styles.emptyStateIcon}>ℹ️</span>
                        <span>{t("NO_SUMMARY_GENERATED")}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}

            {activeTab === "transcript" &&
              (!tabAvailability.transcript && isProcessing ? (
                <div className={styles.processingTabContent}>
                  <div className={styles.processingSpinner} />
                  <div className={styles.processingMessage}>
                    {t("TAB_PROCESSING")}
                  </div>
                </div>
              ) : (
                <div className={styles.tabText}>
                  <div className={styles.transcriptContainer}>
                    {record.transcription?.trim() ? (
                      formatTranscript(record.transcription)
                    ) : (
                      <div className={styles.emptyStateMessage}>
                        <span className={styles.emptyStateIcon}>ℹ️</span>
                        <span>{t("NO_TRANSCRIPTION_GENERATED")}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>

        <div className={styles.actionButtons}>
          <button
            className={styles.primaryBtn}
            onClick={handleExportPdf}
            disabled={isExporting}
            dir={summaryModalDir}
          >
            {isExporting ? (
              <>
                <span className={styles.savingSpinner} />
                <span>{t("EXPORTING")}</span>
              </>
            ) : (
              <span>{t("EXPORT_TO_PDF")}</span>
            )}
          </button>
          <button className={styles.secondaryBtn} onClick={onClose}>
            <span>❌</span>
            <span>{t("CLOSE")}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
